import React from "react";
import { File } from "../../models/download-model";
import {
    IconButton,
    ListItem,
    ListItemText,
    useTheme,
    Divider
} from "@mui/material";
import GetAppIcon from "@mui/icons-material/GetApp";
import { downloadAuthenticatedFile } from "../../api/tasks";
import { BASE_URL } from "../../api";

/**
 * Props interface for the FileItemComponent.
 * 
 * @interface FileItemProps
 * @property {File} item - The file object containing fileName and fileUrl
 * @property {number} index - The index of the item in the list
 */
interface FileItemProps {
    item: File;
    index: number;
}

/**
 * A component that renders a single file item in a download list.
 * Features include:
 * - File name display with responsive text truncation
 * - Download button with icon
 * - Responsive layout for different screen sizes
 * - Theme-aware styling
 * - Automatic file download on click
 * 
 * @component
 * @param {FileItemProps} props - Component props
 * @param {File} props.item - The file object containing fileName and fileUrl
 * @param {number} props.index - The index of the item in the list
 * @returns {JSX.Element} The rendered file item component
 * 
 * @example
 * ```tsx
 * <FileItemComponent 
 *   item={{ fileName: "example.pdf", fileUrl: "/files/example.pdf" }} 
 *   index={0} 
 * />
 * ```
 */


const FileItemComponent = ({ item, index }: FileItemProps) => {
    const theme = useTheme();
    const { fileName, fileUrl } = item;

    return (
        <React.Fragment key={index}>

            <ListItem
                button
                onClick={() => downloadAuthenticatedFile(`${BASE_URL}/secure-downloads/${encodeURIComponent(fileUrl)}`, {}, fileName)}
                sx={{
                    listStyle: 'outside',
                    textDecoration: 'none',
                    width: 'fit-content',
                }}>
                <ListItemText
                    sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: { xs: 'normal', sm: 'nowrap' },
                        wordBreak: { xs: 'break-word', sm: 'normal' },
                        maxWidth: { xs: '200px', sm: 'none' },
                        flex: 'none',
                        color: theme.palette.mode === 'dark' ? 'white' : 'black',
                    }}
                    primary={fileName}
                />
                <IconButton edge="end">
                    <GetAppIcon />
                </IconButton>
            </ListItem>

            <Divider orientation="horizontal" variant="middle" />
        </React.Fragment>
    );
};

export default FileItemComponent;